import React, { useState, useRef } from 'react';
import '../css/textFieldInline.css';

export interface TextFieldInlineProps {
  id?: string;
  name?: string;
  value?: string;
  placeholder?: string;
  onChange?: (value: string) => void;
  onFocus?: (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  onBlur?: (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  required?: boolean;
  disabled?: boolean;
  className?: string;
  multiline?: boolean;
  rows?: number;
  maxLength?: number;
  assistiveText?: string;
  error?: boolean;
  errorText?: string;
  warning?: boolean;
  warningText?: string;
  type?: 'text' | 'email' | 'password' | 'number' | 'tel' | 'url';
}

const TextFieldInline: React.FC<TextFieldInlineProps> = ({
  id,
  name,
  value = '',
  placeholder,
  onChange,
  onFocus,
  onBlur,
  required = false,
  disabled = false,
  className = '',
  multiline = false,
  rows = 3,
  maxLength,
  assistiveText,
  error = false,
  errorText,
  warning = false,
  warningText,
  type = 'text',
}) => {
  const [isFocused, setIsFocused] = useState(false);
  const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement>(null);

  const handleFocus = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setIsFocused(true);
    if (onFocus) onFocus(e);
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setIsFocused(false);
    if (onBlur) onBlur(e);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if (onChange) onChange(e.target.value);
  };

  const handlePlateClick = () => {
    inputRef.current?.focus();
  };

  const assistiveMessage = error && errorText
    ? errorText
    : !error && warning && warningText //NOSONAR
    ? warningText
    : assistiveText;

  const assistiveClass = error
    ? 'scl--cms-components-textFieldInline-assistive--error'
    : warning //NOSONAR
    ? 'scl--cms-components-textFieldInline-assistive--warning'
    : '';

  const plateClasses = [
    'scl--cms-components-textFieldInline-plate',
    multiline ? 'scl--cms-components-textFieldInline--multiline' : '',
    isFocused ? 'scl--cms-components-textFieldInline--focused' : '',
    error     ? 'scl--cms-components-textFieldInline--error'   : '',
    warning && !error ? 'scl--cms-components-textFieldInline--warning'  : '',
    disabled  ? 'scl--cms-components-textFieldInline--disabled' : '',
  ].filter(Boolean).join(' ');

  return (
    <div className={`scl--cms-components-textFieldInline-container ${className}`}>
      <div className={plateClasses} onClick={handlePlateClick} //NOSONAR
      >
        <div className="scl--cms-components-textFieldInline-wrapper">
          {multiline ? (
            <textarea
              ref={inputRef as React.RefObject<HTMLTextAreaElement>}
              id={id}
              name={name}
              value={value}
              rows={rows}
              maxLength={maxLength}
              disabled={disabled}
              placeholder={placeholder}
              onChange={handleChange}
              onFocus={handleFocus}
              onBlur={handleBlur}
              className="scl--cms-components-textFieldInline-input scl--cms-components-textFieldInline-input--textarea"
              autoComplete="off"
            />
          ) : (
            <input
              ref={inputRef as React.RefObject<HTMLInputElement>}
              id={id}
              name={name}
              type={type}
              value={value}
              maxLength={maxLength}
              disabled={disabled}
              required={required}
              placeholder={placeholder}
              onChange={handleChange}
              onFocus={handleFocus}
              onBlur={handleBlur}
              className="scl--cms-components-textFieldInline-input"
              autoComplete="off"
            />
          )}
        </div>
      </div>

      {assistiveMessage && (
        <div className={`scl--cms-components-textFieldInline-assistive ${assistiveClass}`}>
          {assistiveMessage}
        </div>
      )}
    </div>
  );
};

export default TextFieldInline;